import { Controller } from "@hotwired/stimulus"
import { parseLocalizedNumber, localizeNumber } from "../../util"

/**
 * Form controller allows to trigger submit of a form. The controlled element
 * must be a <form>.
 *
 * The format number function allows to check if the content of a textual input
 * is not null and to transform it into a number
 *
 * `toggleChip` allows to control checkbox chips rendered by the default form
 * helper. The desired visual style requires adding a class to a container of
 * a checkbox based on its state. That is achieved through this action.
 *
 * `guardedElement` in combination with `data-form-guard-button=true` allows
 *  form to guard some element from being visible after the form changes.
 *
 *  %form{data: { controller: :form, currentClass: 'current' }}
 *    %fieldset
 *      %div
 *        %label
 *          %input{type: :checkbox, data: { action: 'change->form#toggleChip' }}
 *          Option A
 *      %div.current
 *        %label
 *          %input{type: :checkbox, data: { action: 'change->form#toggleChip' }, checked: :checked}
 *          Option B
 *
 */
export default class extends Controller {
  static targets = ["checkboxChip", "guardedElement"]
  static classes = ["current", "hidden"]
  static values = {
    guardElement: { type: Boolean, default: false },
  }

  connect() {
    if (!this.guardElementValue) {
      return
    }

    if (!this.hasGuardedElementTarget) {
      return
    }

    this.boundUpdateGuardedElement = this.updateGuardedElement.bind(this)
    this.element.addEventListener("input", this.boundUpdateGuardedElement)
    this.element.addEventListener("change", this.boundUpdateGuardedElement)
  }

  disconnect() {
    if (!this.boundUpdateGuardedElement) {
      return
    }

    this.element.removeEventListener("input", this.boundUpdateGuardedElement)
    this.element.removeEventListener("change", this.boundUpdateGuardedElement)
  }

  submit() {
    this.element.submit()
  }

  requestSubmit() {
    this.element.requestSubmit()
  }

  formatNumber(event) {
    if (event.target.type === "text") {
      const val = event.target.value
      if (val === "") {
        return
      }

      event.target.value = localizeNumber(parseLocalizedNumber(val))
    } else {
      console.error(`unsupported input`)
    }
  }

  toggleChip(e) {
    const chip = e.currentTarget.closest("[data-form-target=checkboxChip]")
    if (!chip) {
      return
    }

    chip.classList.toggle(this.currentClass, e.currentTarget.checked)
  }

  updateGuardedElement() {
    if (!this.hasGuardedElementTarget) {
      return
    }

    this.guardedElementTarget.classList.add(this.hiddenClass)
  }
}
